import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import TagsFilter from '../TagFilter/TagsFilter';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import CareApi, { SearchCareParam } from './api/CareApi';
import CareModel from './models/CareModel';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import { CareSearchSelector, resetCareSearch, setBroker, setCareSearchResults, setCity, setClassifications, setClientNames, setCountries, setPicName, setSearchResultTotalRecord, setTriggerCareSearch, setTriggerFocusCompanyName, setYear } from './reducers/CareSearchReducer';
import { userSelectorSelector } from '../UserSelector/reducers/UserSelectorReducer';
import UserSelector from '../UserSelector/UserSelector';
import { ClassificationEnum } from './models/ClassificationEnum';
import { setCareYear } from './reducers/CareInputReducer';
import { getYears } from '../MyFixtures/MyFixtureSearch';
import { CompanyNamePICSelectorSelector, setCompanyNamePICSearchResults } from '../CompanyNamePICSelector/reducers/CompanyNamePICSelectorReducer';
import CompanyNamePICApi from '../CompanyNamePIC/api/CompanyNamePICApi';
import CompanyNamePICModel from '../CompanyNamePIC/models/CompanyNamePICModel';

const CareSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const careSearchData = useSelector(CareSearchSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    const userSelectorData = useSelector(userSelectorSelector);
    const companyNamePICSelectorData = useSelector(CompanyNamePICSelectorSelector);

    useEffect(() => {
        if (companyNamePICSelectorData.searchResults.length > 0) {
            return;
        }

        dispatch(showLoading(true));
        CompanyNamePICApi.GetAll()
            .then((response: BaseResponse<CompanyNamePICModel[]>) => {
                if (response.success) {
                    dispatch(setCompanyNamePICSearchResults(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            })
    }, []);

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Care,
                    queryJson: JSON.stringify(careSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        dispatch(showLoading(true));

        CareApi.Search({
            query: careSearchData.query
        } as SearchCareParam)
            .then((response: BaseResponse<CareModel[]>) => {
                if (response.success) {
                    dispatch(setCareSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [careSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Care}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerCareSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetCareSearch());
                                                dispatch(setTriggerFocusCompanyName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <Autocomplete
                        disableClearable={true}
                        fullWidth
                        options={getYears()}
                        renderInput={(params) => <TextField {...params} label="Year" />}
                        className='tonnage-search-item'
                        getOptionLabel={(option: number) => {
                            return option.toString();
                        }}
                        value={careSearchData.query.year}
                        onChange={(event: React.SyntheticEvent, value: number, reason: string, details?: AutocompleteChangeDetails<number> | undefined) => {
                            dispatch(setYear(value));
                            dispatch(setCareYear(value));
                        }}
                    />
                    <TagsFilter
                        data={_.map(_.groupBy(companyNamePICSelectorData.searchResults, (item) => item.companyName), (companyNameGroup) => {
                            return {
                                id: companyNameGroup[0].companyName,
                                value: companyNameGroup[0].companyName,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Company Name"
                        className="tonnage-search-item"
                        value={careSearchData.query.clientNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setClientNames(value));
                        }}
                        triggerFocusInput={careSearchData.triggerFocusCompanyName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.countries, (item) => {
                            return {
                                id: item.code,
                                value: item.name,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Country"
                        className="tonnage-search-item"
                        value={careSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value))
                        }}
                    ></TagsFilter>
                    <TextField
                        className='tonnage-search-item'
                        fullWidth
                        label="City"
                        value={careSearchData.query.city}
                        onChange={(event) => {
                            dispatch(setCity(event.target.value));
                        }}
                    >
                    </TextField>
                    <TextField
                        className='tonnage-search-item'
                        fullWidth
                        label="PIC Name"
                        value={careSearchData.query.picName}
                        onChange={(event) => {
                            dispatch(setPicName(event.target.value));
                        }}
                    >
                    </TextField>
                    <UserSelector
                        label='Broker'
                        className='tonnage-search-item'
                        selectedUserId={careSearchData.query.broker}
                        onUserIdChange={(value: number) => {
                            dispatch(setBroker(value));
                        }}
                    >
                    </UserSelector>
                    <TagsFilter
                        data={_.map([ClassificationEnum.Silver, ClassificationEnum.Titanium, ClassificationEnum.Gold, ClassificationEnum.Platinum, ClassificationEnum.Diamond], (item) => {
                            return {
                                id: item,
                                value: item,
                                positive: true
                            } as QueryItem
                        })}
                        label="Auto Class"
                        className="tonnage-search-item"
                        value={careSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setClassifications(value))
                        }}
                    ></TagsFilter>
                </div>
            </CustomAccordion>
        </>
    )
};

export default CareSearch;